import type { AiListingAutofillResponse } from '../../types/aiListingAutofill';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { PROPERTY_AD_AMENITY_FIELDS } from './propertyAdAmenities';

export type AiReviewTarget =
  | keyof PropertyAdEditorFormValues
  | 'location'
  | 'section:pricing'
  | 'section:specifications'
  | 'section:amenities'
  | 'section:commission'
  | 'section:general';

export interface AiReviewAttention {
  field: string;
  reason: string;
  target: AiReviewTarget;
}

const EDITABLE_FIELDS: ReadonlyArray<keyof PropertyAdEditorFormValues> = [
  'activityKind',
  'transactionType',
  'completionStatus',
  'propertyKind',
  'builtUpArea',
  'beds',
  'baths',
  'floor',
  'furniture',
  'renovation',
  'occupancy',
  'view',
  'price',
  'sellingPrice',
  'currency',
  'multilisting',
  'rentedTill',
  'toSeller',
  'postHandover',
  'handover',
  'topUp',
  'topUpConditions',
  'priceYearly',
  'priceMonthly',
  'priceWeekly',
  'priceDaily',
  'defaultRentFrequency',
  'addCommission',
  'currencyCommission',
  'description',
  ...PROPERTY_AD_AMENITY_FIELDS,
];

const LOCATION_FIELDS = new Set([
  'location',
  'locationId',
  'addressEn',
  'nameEn',
  'placeId',
  'districtId',
  'buildingName',
  'city',
  'community',
  'subcommunity',
  'buildingOrProject',
  'coordinatesLat',
  'coordinatesLong',
]);

const REASON_MESSAGES: Record<string, string> = {
  defaulted_activity_kind: 'Sale was selected by default. Please verify it.',
  inferred_activity_from_rental_pricing:
    'Rent was selected based on the rental pricing. Please verify it.',
  defaulted_transaction_type: 'Ready was selected by default. Please verify it.',
  defaulted_property_kind: 'Apartment was selected by default. Please verify it.',
  unsupported_activity_evidence: 'Please confirm whether this listing is for sale or rent.',
  living_area_not_built_up_area: 'Please enter the built-up or total area manually.',
  ambiguous_transaction_type: 'Please confirm the transaction type.',
  unsupported_completion_evidence: 'Please confirm the completion status.',
  missing_activity_kind: 'Please select Sale or Rent to confirm the pricing details.',
  unsafe_project_attribution: 'Please verify the property location.',
  ambiguous_project_descriptor: 'Please verify the property location.',
  parking_not_multilisting: 'Please confirm the listing coverage option.',
};

export const getAiReviewMessage = (code: string): string =>
  Object.prototype.hasOwnProperty.call(REASON_MESSAGES, code)
    ? REASON_MESSAGES[code]
    : 'Please verify this value.';

export const getAiReviewTarget = (field: string, reason = ''): AiReviewTarget => {
  if (field === 'prices') {
    return reason === 'missing_activity_kind' ? 'activityKind' : 'section:pricing';
  }

  const name = field.split('.').pop() ?? field;
  if (LOCATION_FIELDS.has(name) || field.startsWith('location.')) return 'location';
  if (name === 'plotArea') return 'section:specifications';
  if (name === 'toDeveloper') return 'section:pricing';
  if (name === 'amenities') return 'section:amenities';
  if (name === 'commissions' || name === 'commission') return 'section:commission';
  if (EDITABLE_FIELDS.includes(name as keyof PropertyAdEditorFormValues)) {
    return name as keyof PropertyAdEditorFormValues;
  }
  return 'section:general';
};

export const createAiReviewAttention = (
  review?: AiListingAutofillResponse['review'] | null
): AiReviewAttention[] => {
  if (!review?.required) return [];

  return review.fields.flatMap(({ field, reasons }) =>
    (reasons.length > 0 ? reasons : [{ code: '' }]).map(({ code }) => ({
      field,
      reason: code,
      target: getAiReviewTarget(field, code),
    }))
  );
};

export const getAiReviewMessages = (
  attention: readonly AiReviewAttention[],
  target: AiReviewTarget
): string[] =>
  Array.from(
    new Set(
      attention
        .filter((item) => item.target === target)
        .map((item) => getAiReviewMessage(item.reason))
    )
  );

export const getAiSectionReviewItems = (
  attention: readonly AiReviewAttention[],
  target: AiReviewTarget
): Array<{ field: string; messages: string[] }> =>
  Array.from(
    new Set(attention.filter((item) => item.target === target).map((item) => item.field))
  ).map((field) => ({
    field,
    messages: getAiReviewMessages(
      attention.filter((item) => item.field === field),
      target
    ),
  }));

export const markAiReviewItemReviewed = (
  attention: readonly AiReviewAttention[],
  field: string,
  target: AiReviewTarget
): AiReviewAttention[] =>
  attention.filter((item) => item.field !== field || item.target !== target);
